import { loadRazorpayCheckoutScript, openRazorpayOrderCheckout } from './razorpayCheckout';
import { track } from './analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Same subscribe -> Razorpay modal -> poll-for-webhook flow as BillingPage, for the /checkout page.

interface SubscribeResult {
  subscription: { status: string } | null;
  checkout: {
    keyId: string;
    subscriptionId: string;
    shortUrl: string | null;
  };
}

interface RazorpaySubscriptionOptions {
  key: string;
  subscription_id: string;
  name: string;
  description: string;
  notes?: Record<string, string>;
  theme?: { color: string };
  modal?: { ondismiss?: () => void };
  handler?: (response: { razorpay_payment_id?: string }) => void;
}

interface RazorpaySubscriptionInstance {
  open: () => void;
  on: (event: 'payment.failed', handler: (response: { error?: { code?: string; description?: string } }) => void) => void;
}

type RazorpaySubscriptionWindow = Window & {
  Razorpay?: new (options: RazorpaySubscriptionOptions) => RazorpaySubscriptionInstance;
};

export type SubscriptionCheckoutOutcome = 'activated' | 'free' | 'pending' | 'external';

const waitFor = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function hasLiveSubscription(): Promise<boolean> {
  const res = await fetch(`${API_BASE}/api/me/billing/subscription`, { credentials: 'include' });
  if (!res.ok) return false;
  const data = (await res.json()) as { subscription: { status: string } | null };
  const status = data.subscription?.status?.toLowerCase();
  return Boolean(data.subscription && status !== 'cancelled' && status !== 'completed');
}

export async function hasBillingAddress(): Promise<boolean> {
  const res = await fetch(`${API_BASE}/api/me/billing-address`, { credentials: 'include' });
  if (!res.ok) throw new Error('Could not check your billing address. Please try again.');
  const data = (await res.json()) as { address: unknown | null };
  return Boolean(data.address);
}

// Autopay off: pay one month through a Razorpay order (no mandate, no renewal), then confirm it so the plan activates.
export async function runOneTimePlanCheckout(input: { planId: string; planName: string; onStatus?: (message: string) => void }): Promise<SubscriptionCheckoutOutcome> {
  const { planId, planName, onStatus } = input;
  track('begin_checkout', { plan_id: planId, plan_name: planName, billing: 'one_time' });
  const orderResponse = await fetch(`${API_BASE}/api/me/billing/plan-order`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planId }),
  });
  if (!orderResponse.ok) {
    const data = await orderResponse.json().catch(() => ({ error: 'Unable to start checkout' })) as { error?: string };
    throw new Error(data.error || 'Unable to start checkout');
  }
  const order = (await orderResponse.json()) as {
    checkout: {
      keyId: string;
      orderId: string;
      amountSubunits: number;
      currency: string;
      name: string;
      description: string;
      prefill?: { email?: string; name?: string | null };
    };
  };

  const paid = await openRazorpayOrderCheckout({
    keyId: order.checkout.keyId,
    orderId: order.checkout.orderId,
    amountSubunits: order.checkout.amountSubunits,
    currency: order.checkout.currency,
    name: order.checkout.name,
    description: order.checkout.description || `${planName} - 1 month`,
    prefill: order.checkout.prefill,
    notes: { planId, source: 'plan_one_time' },
  });
  if (!paid.razorpay_payment_id || !paid.razorpay_order_id || !paid.razorpay_signature) {
    throw new Error('Payment did not complete.');
  }

  onStatus?.('Payment received. Activating your plan…');
  const confirmResponse = await fetch(`${API_BASE}/api/me/billing/plan-order/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      razorpayOrderId: paid.razorpay_order_id,
      razorpayPaymentId: paid.razorpay_payment_id,
      razorpaySignature: paid.razorpay_signature,
    }),
  });
  if (!confirmResponse.ok) {
    const data = await confirmResponse.json().catch(() => ({ error: 'Could not confirm your payment' })) as { error?: string };
    throw new Error(data.error || 'Could not confirm your payment');
  }
  track('purchase', {
    transaction_id: paid.razorpay_order_id,
    value: order.checkout.amountSubunits / 100,
    currency: order.checkout.currency,
    plan_id: planId,
    plan_name: planName,
    billing: 'one_time',
  });
  return 'activated';
}

export async function runSubscriptionCheckout(input: {
  planId: string;
  planName: string;
  oneTime: boolean;
  onStatus?: (message: string) => void;
}): Promise<SubscriptionCheckoutOutcome> {
  const { planId, planName, oneTime, onStatus } = input;
  if (oneTime) return runOneTimePlanCheckout({ planId, planName, onStatus });
  track('begin_checkout', { plan_id: planId, plan_name: planName, billing: 'subscription' });

  const endpoint = (await hasLiveSubscription())
    ? `${API_BASE}/api/me/billing/change-plan`
    : `${API_BASE}/api/me/billing/subscribe`;

  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planId }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Unable to start checkout' })) as { error?: string };
    throw new Error(data.error || 'Unable to start checkout');
  }
  const data = (await response.json()) as SubscribeResult;

  if (!data.checkout.keyId && !data.checkout.subscriptionId) return 'free';

  let modalLaunched = false;
  try {
    await loadRazorpayCheckoutScript();
    const Razorpay = (window as RazorpaySubscriptionWindow).Razorpay;
    if (!Razorpay) throw new Error('Razorpay Checkout SDK failed to initialize');

    if (data.checkout.keyId && data.checkout.subscriptionId) {
      modalLaunched = true;
      await new Promise<void>((resolve, reject) => {
        const checkout = new Razorpay({
          key: data.checkout.keyId,
          subscription_id: data.checkout.subscriptionId,
          name: 'Viral AI',
          description: `${planName} subscription`,
          notes: { planId, source: 'checkout_page_subscription' },
          theme: { color: '#10B981' },
          modal: { ondismiss: () => reject(new Error('Checkout closed before payment was completed.')) },
          handler: (checkoutResponse) => {
            if (!checkoutResponse.razorpay_payment_id) {
              reject(new Error('Subscription authorization did not complete.'));
              return;
            }
            resolve();
          },
        });
        checkout.on('payment.failed', (failure) => {
          const code = failure.error?.code ? ` (${failure.error.code})` : '';
          reject(new Error((failure.error?.description || 'Subscription payment failed') + code));
        });
        checkout.open();
      });
    }
  } catch (err) {
    if (!modalLaunched && data.checkout.shortUrl) {
      const checkoutWindow = window.open(data.checkout.shortUrl, '_blank', 'noopener,noreferrer');
      if (!checkoutWindow) window.location.href = data.checkout.shortUrl;
      return 'external';
    }
    throw err;
  }

  // The webhook can lag a few seconds behind checkout success.
  track('purchase', { plan_id: planId, plan_name: planName, billing: 'subscription' });
  onStatus?.('Payment received. Activating your plan…');
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const res = await fetch(`${API_BASE}/api/me/billing/subscription`, { credentials: 'include' });
    if (res.ok) {
      const latest = (await res.json()) as { subscription: { status: string } | null };
      if (latest.subscription?.status?.toLowerCase() === 'active') return 'activated';
    }
    if (attempt < 3) await waitFor(1500);
  }
  return 'pending';
}
